import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import WorkOrderDashboard from './components/WorkOrderDashboard';
import WorkOrderCreation from './components/WorkOrderCreation';
import TechnicianDispatch from './components/TechnicianDispatch';
import PartsInventoryPanel from './components/PartsInventoryPanel';
import { equipmentLifecycleService } from '../../lib/equipmentLifecycleService';

const WorkOrderManagementSystem = () => {
  const { userProfile } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [partsInventory, setPartsInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const hasAccess = ['owner', 'manager', 'sysadmin', 'staff']?.includes(userProfile?.role);
  const gymId = userProfile?.gym_id;

  useEffect(() => {
    if (hasAccess && gymId) {
      loadData();
    }
  }, [hasAccess, gymId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workOrdersRes, equipmentRes] = await Promise.all([
        equipmentLifecycleService?.getWorkOrders(gymId),
        equipmentLifecycleService?.getEquipment(gymId)
      ]);

      if (workOrdersRes?.data) setWorkOrders(workOrdersRes?.data);
      if (equipmentRes?.data) setEquipment(equipmentRes?.data);

      // Mock technicians data
      setTechnicians([
        { id: '1', name: 'John Smith', skills: ['HVAC', 'Electrical'], availability: 'available', location: 'Building A', workload: 2 },
        { id: '2', name: 'Sarah Johnson', skills: ['Plumbing', 'General'], availability: 'busy', location: 'Building B', workload: 5 },
        { id: '3', name: 'Mike Davis', skills: ['Equipment', 'Electrical'], availability: 'available', location: 'Building A', workload: 1 },
        { id: '4', name: 'Emily Chen', skills: ['HVAC', 'Equipment'], availability: 'on-break', location: 'Building C', workload: 3 }
      ]);

      // Mock parts inventory
      setPartsInventory([
        { id: '1', name: 'HVAC Filter', category: 'HVAC', stock: 15, minStock: 10, status: 'in-stock' },
        { id: '2', name: 'Treadmill Belt', category: 'Equipment', stock: 3, minStock: 5, status: 'low-stock' },
        { id: '3', name: 'Cable Assembly', category: 'Equipment', stock: 0, minStock: 2, status: 'out-of-stock' },
        { id: '4', name: 'LED Bulbs', category: 'Electrical', stock: 25, minStock: 15, status: 'in-stock' },
        { id: '5', name: 'Pipe Fittings', category: 'Plumbing', stock: 8, minStock: 10, status: 'low-stock' }
      ]);
    } catch (error) {
      console.error('Error loading work order data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <DashboardNavigation />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-card rounded-xl p-8 border border-border text-center">
            <Icon name="ShieldAlert" size={48} className="mx-auto mb-4 text-error" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You do not have permission to access work order management.</p>
          </div>
        </div>
      </div>
    );
  }

  const views = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'dispatch', label: 'Dispatch', icon: 'Send' },
    { id: 'inventory', label: 'Parts', icon: 'Package' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Work Order Management</h1>
            <p className="text-sm md:text-base text-muted-foreground">Comprehensive maintenance request processing and coordination</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={loadData}
              size="sm"
            >
              <span className="hidden md:inline">Refresh</span>
            </Button>
            <Button
              variant="primary"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowCreateModal(true)}
              size="sm"
            >
              Create Work Order
            </Button>
          </div>
        </div>

        {/* View Tabs - Mobile Optimized */}
        <div className="bg-card rounded-xl border border-border mb-6">
          <div className="flex items-center gap-1 p-2 overflow-x-auto custom-scrollbar">
            {views?.map((view) => (
              <button
                key={view?.id}
                onClick={() => setActiveView(view?.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-2.5 rounded-lg whitespace-nowrap transition-all text-sm md:text-base ${
                  activeView === view?.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={view?.icon} size={18} />
                <span className="font-medium">{view?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading work orders...</p>
            </div>
          </div>
        ) : (
          <>
            {activeView === 'dashboard' && (
              <WorkOrderDashboard
                workOrders={workOrders}
                equipment={equipment}
                technicians={technicians}
                onRefresh={loadData}
              />
            )}
            {activeView === 'dispatch' && (
              <TechnicianDispatch
                workOrders={workOrders}
                technicians={technicians}
                equipment={equipment}
                onRefresh={loadData}
              />
            )}
            {activeView === 'inventory' && (
              <PartsInventoryPanel
                partsInventory={partsInventory}
                workOrders={workOrders}
                onRefresh={loadData}
              />
            )}
          </>
        )}
      </div>

      {/* Create Work Order Modal */}
      {showCreateModal && (
        <WorkOrderCreation
          equipment={equipment}
          technicians={technicians}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default WorkOrderManagementSystem;